import { useId } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import PropTypes from "prop-types";

/**
 * A select dropdown component integrated with React Hook Form.
 *
 * @param {object} props - The component props.
 * @param {string} props.name - The name of the field.
 * @param {string} props.label - The display label for the field.
 * @param {Array<object>} props.options - An array of options for the dropdown.
 * @param {string} props.options[].key - The value for the option.
 * @param {string} props.options[].label - The display text for the option.
 * @returns {JSX.Element}
 */
const FormSelect = ({ name, label, options, ...otherProps }) => {
  const { control } = useFormContext();
  const id = useId();

  const labelId = `${id}-${name}-label`;
  const selectId = `${id}-${name}-select`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth error={!!error}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            {...field}
            label={label}
            id={selectId}
            labelId={labelId}
            {...otherProps}
          >
            {options.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

FormSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default FormSelect;
